// The two ends of the authentication path that had no screen at all.
//
// caiet UC10-06 — an account MPass authenticated whose attributes map to no role
// SIMSM honours. Until this page existed the AssertionConsumerService threw
// before it set a cookie and the browser was handed a bare HTTP 500: no session,
// no redirect, nothing to read. Its sibling state, an expired session, was worse
// for being silent — every 401 hard-redirected to the landing page without a
// word about what had happened (audit-system-admin-2026-08-30 S5).
//
// Shaped after `wireframes/react/src/pages/EroareAutorizare.tsx` (read-only, R6):
// the trust strip with the language switcher, the 401 watermark, a heading, the
// explanation, the support contact in an info box, and two ways forward. The
// wireframe draws the no-role screen only; the expired session reuses its
// composition, because a state the wireframe does not draw is not a licence to
// invent a second layout for the same kind of dead end.
//
// The copy shipped in ro/ru/en months ago and had zero references —
// `auth.fallback` and `auth.session_expired`, both written as one glued
// paragraph. Every word of both is on these two screens now, split at the
// sentence boundary the wireframe draws (a heading, then the explanation), which
// is why the glued originals are gone rather than referenced: a key nothing
// renders is the orphan the audit reported in the first place.
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslate } from '@tolgee/react';
import { MudButton, MudInfoBox, MudLink } from '@egov-moldova/mud-react';
import { LOGIN_REFUSAL_CAUSES, type LoginRefusalCause } from '@simsm/shared-types';

export type AuthNoticeKind = 'no-role' | 'session-expired';

const SUPPORT_PORTAL_URL = 'https://support.amdm.gov.md';

// caiet UC10-06, TC-UC10-06-02: the bff sends each cause of the refusal as a
// `reason` parameter, and the screen states it under the drawn explanation. A
// value outside the list is not rendered, so the URL selects copy and never
// becomes copy (docs/shape-ops-login-refusal-reason.md §2-3).
const CAUSE_KEYS: Record<LoginRefusalCause, string> = {
  NO_KNOWN_ROLE: 'auth.no_role.cause.no_known_role',
  REGISTRAR_INACTIVE: 'auth.no_role.cause.registrar_inactive',
  REGISTRAR_UNKNOWN: 'auth.no_role.cause.registrar_unknown',
  LOCATION_INACTIVE: 'auth.no_role.cause.location_inactive',
  LOCATION_UNKNOWN: 'auth.no_role.cause.location_unknown',
  CODE_MALFORMED: 'auth.no_role.cause.code_malformed',
  NO_ASSOCIATION: 'auth.no_role.cause.no_association',
};

export function AuthNoticePage({ kind }: { kind: AuthNoticeKind }): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const noRole = kind === 'no-role';
  const reasons = new Set(searchParams.getAll('reason'));
  const causes = noRole ? LOGIN_REFUSAL_CAUSES.filter((cause) => reasons.has(cause)) : [];

  function signInAgain(): void {
    window.location.href = '/api/v1/auth/login';
  }

  return (
    <main className="simsm-auth-notice">
      {/* The gov.md strip is `PublicShell`'s now, above this page rather than
          inside it. */}
      <div className="simsm-auth-notice__panel">
        {/* The wireframe's oversized status numeral is decoration: the heading
            below states the same thing in words, so announcing "401" as well
            only makes the page read worse. */}
        <p className="simsm-auth-notice__code" aria-hidden="true">
          {t('auth.notice.status_code')}
        </p>
        <h1 className="simsm-auth-notice__title">
          {t(noRole ? 'auth.no_role.title' : 'auth.expired.title')}
        </h1>
        <p className="simsm-auth-notice__body">
          {t(noRole ? 'auth.no_role.detail' : 'auth.expired.detail')}
        </p>
        {causes.length > 0 ? (
          <p className="simsm-auth-notice__body">
            {causes.map((cause) => t(CAUSE_KEYS[cause])).join(' ')}
          </p>
        ) : null}

        {/* The drawn box is blue: `.info-box.info` is `--bg-brand-sec`
            (#e8f0fb) with the icon in `--bg-brand` (`mud.css:821-826`). In AGE
            that is `emphasis="strong"` — the default `subtle` is
            `--info-box-subtle-info-moderate-background`, #f5f5f5, the grey the
            parity recapture photographed (A-19) — and `info-moderate`, the
            variant documented as carrying the brand-blue icon. Two props, the
            same two values, and no token declared here (R2). */}
        <MudInfoBox
          variant="info-moderate"
          emphasis="strong"
          className="simsm-auth-notice__support"
        >
          <span>
            {t('auth.notice.support.prefix')}
            <MudLink href={`mailto:${t('footer.contact.email')}`}>
              {t('footer.contact.email')}
            </MudLink>
            {t('auth.notice.support.middle')}
            {/* No `target="_blank"`: mud-link's external indicator renders
                between the label and the full stop that closes the sentence, and
                a floating period reads as a typo. The reader is on a dead end
                anyway — there is no work behind this page to keep open. */}
            <MudLink href={SUPPORT_PORTAL_URL}>{t('footer.link.support_portal')}</MudLink>
            {t('auth.notice.support.suffix')}
            {/* Only the no-role denial is journalled, on both paths that reach
                this screen: auth.controller.ts warns before redirecting the SAML
                one here, auth.service.ts warns before refusing the development
                one. An expired session is not an event anyone recorded, so this
                page does not claim it was. */}
            {noRole ? ` ${t('auth.notice.logged')}` : null}
          </span>
        </MudInfoBox>

        <div className="simsm-auth-notice__actions">
          {/* Same two treatments in both states, swapped between the two
              labels: the wireframe makes the landing page the filled action on
              the no-role screen, but for an expired session the action that
              resolves the state is signing in again, so that one leads. */}
          <MudButton
            shape="circular"
            variant={noRole ? 'primary' : 'strict'}
            appearance={noRole ? 'filled' : 'outlined'}
            size="lg"
            onClick={() => navigate('/', { replace: true })}
          >
            {t('auth.notice.action.home')}
          </MudButton>
          <MudButton
            shape="circular"
            variant={noRole ? 'strict' : 'primary'}
            appearance={noRole ? 'outlined' : 'filled'}
            size="lg"
            onClick={signInAgain}
          >
            {t('auth.notice.action.retry')}
          </MudButton>
        </div>
      </div>
    </main>
  );
}
